// Links removed to make Home Featured Projects non-clickable
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/services/api";
import { useTranslation } from "@/context/TranslationContext";

// ─────────────────────────────────────────────────────────────────────────────
// Converts any Google Drive share/view URL into a directly embeddable URL.
// Tries multiple Drive URL patterns. Non-Drive URLs pass through unchanged.
//
// We use the /uc?export=view endpoint instead of /thumbnail because:
//  - /thumbnail blocks cross-origin <img> requests (CORS) in some browsers
//  - /uc?export=view is the standard embeddable export endpoint Drive provides
//  - sz=w1200 gives enough resolution for card display
// ─────────────────────────────────────────────────────────────────────────────
const convertDriveLink = (url: string): string => {
  if (!url) return "";
  let fileId = null;

  // Format: https://drive.google.com/file/d/FILE_ID/view
  const fileMatch = url.match(/\/file\/d\/([^\/]+)/);
  if (fileMatch) {
    fileId = fileMatch[1];
  } else {
    // Format: https://drive.google.com/uc?export=view&id=FILE_ID OR https://drive.google.com/open?id=FILE_ID
    const idMatch = url.match(/[?&]id=([^&]+)/);
    if (idMatch) {
      fileId = idMatch[1];
    }
  }

  if (fileId) {
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w800`;
  }
  return url;
};

// ─────────────────────────────────────────────────────────────────────────────
// ProjectImage — resolves the URL on mount, handles load errors gracefully.
// Two-stage fallback:
//   Stage 1: uc?export=view (primary, best CORS support)
//   Stage 2: thumbnail?sz=w800 (fallback if stage 1 fails)
//   Stage 3: styled placeholder (if both fail)
// ─────────────────────────────────────────────────────────────────────────────
function extractDriveId(url: string): string | null {
  const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return fileMatch[1];
  const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idMatch) return idMatch[1];
  return null;
}

function ProjectImage({
  url,
  alt,
  className,
}: {
  url?: string;
  alt: string;
  className?: string;
}) {
  const [src, setSrc]         = useState(() => convertDriveLink(url || ""));
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setSrc(convertDriveLink(url || ""));
    setFallbackUsed(false);
    setErrored(false);
  }, [url]);

  const handleError = () => {
    // Stage 2: try thumbnail endpoint if uc export failed and we haven't yet
    if (!fallbackUsed && url) {
      const id = extractDriveId(url);
      if (id) {
        setFallbackUsed(true);
        setSrc(`https://drive.google.com/thumbnail?id=${id}&sz=w800`);
        return;
      }
    }
    // Stage 3: give up, show placeholder
    setErrored(true);
  };

  if (!src || errored) {
    return (
      <div
        className={className}
        style={{
          background: "linear-gradient(135deg,#0C1528 0%,#060D1A 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i className="ri-image-line" style={{ fontSize: 36, opacity: 0.15, color: "#00E5FF" }} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      referrerPolicy="no-referrer"
      className={className}
      onError={handleError}
    />
  );
}

interface FeaturedItem {
  id: string;
  title: string;
  description?: string;
  stage: string;
  thumbnail_url?: string;
  video_url?: string;
  video_type?: string;
  tech_stack: string[];
  featured: boolean;
}

export default function FeaturedProjects() {
  const { t } = useTranslation();
  const [featured, setFeatured] = useState<FeaturedItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    api.get("/portfolio")
      .then((res) => {
        const data = res.data.data || [];
        const shuffled = [...data].sort(() => 0.5 - Math.random());
        setFeatured(shuffled.slice(0, 3));
        setTotalCount(data.length);
      })
      .catch(() => {
        setFeatured([]);
        setTotalCount(0);
      });
  }, []);

  return (
    <section className="bg-deep-black py-28 px-4 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-14 gap-4">
          <div>
            <span className="inline-block text-electric-blue text-xs font-semibold uppercase tracking-[0.2em] mb-5 px-3 py-1.5 border border-electric-blue/25 rounded-full bg-electric-blue/5">
              {t("home_featured.badge")}
            </span>
            <h2
              className="font-black text-white leading-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
            >
              {t("home_featured.title")}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-silver text-sm font-medium transition-all duration-200 whitespace-nowrap group">
            {t("home_featured.view_all")}
            <span className="w-7 h-7 flex items-center justify-center rounded-full border border-dark-border group-hover:border-electric-blue/40 transition-colors">
              <i className="ri-arrow-right-line text-xs" />
            </span>
          </div>
        </div>

        {/* Asymmetric grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

          {/* Large featured card */}
          {featured[0] && (
            <Link
              to={`/portfolio/${featured[0].id}`}
              className="lg:col-span-7 relative rounded-2xl overflow-hidden block hover:shadow-xl transition-shadow group"
            >
              <div className="relative h-80 lg:h-[480px] overflow-hidden">
                <ProjectImage
                  url={featured[0].thumbnail_url}
                  alt={featured[0].title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-deep-black via-deep-black/40 to-transparent" />

                {/* Top badges */}
                <div className="absolute top-5 left-5 flex gap-2">
                  <span className="text-xs px-3 py-1.5 rounded-full bg-dark-card/80 backdrop-blur-sm border border-dark-border text-electric-blue font-medium">
                    {featured[0].stage}
                  </span>
                  <span className="text-xs px-3 py-1.5 rounded-full bg-cyber-orange/80 text-white font-semibold">
                    {t("home_featured.featured_badge")}
                  </span>
                </div>

                {/* Content overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {(featured[0].tech_stack || []).slice(0, 4).map((t) => (
                      <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-electric-blue/10 text-electric-blue border border-electric-blue/20">
                        {t}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-white font-black text-2xl mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {featured[0].title}
                  </h3>
                  <p className="text-silver text-sm leading-relaxed line-clamp-2 max-w-lg">
                    {featured[0].description}
                  </p>
                </div>
              </div>
            </Link>
          )}

          {/* Right column — two stacked cards */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            {featured.slice(1, 3).map((project) => (
              <Link
                key={project.id}
                to={`/portfolio/${project.id}`}
                className="relative rounded-2xl overflow-hidden block flex-1 hover:shadow-xl transition-shadow group"
              >
                <div className="relative h-56 overflow-hidden">
                  <ProjectImage
                    url={project.thumbnail_url}
                    alt={project.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-deep-black via-deep-black/30 to-transparent" />

                  {/* Stage badge */}
                  <div className="absolute top-4 right-4">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-dark-card/80 backdrop-blur-sm border border-dark-border text-electric-blue font-medium">
                      {project.stage}
                    </span>
                  </div>

                  {/* Content overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(project.tech_stack || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-xs px-2 py-0.5 rounded bg-electric-blue/10 text-electric-blue border border-electric-blue/20">
                          {t}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-white font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {project.title}
                    </h3>
                    <p className="text-silver text-xs mt-1 line-clamp-1 leading-relaxed">
                      {project.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-dark-border">
          <p className="text-silver-dim text-sm">
            {t("home_featured.showing")}{" "}
            <strong className="text-white">{featured.length}</strong> {" "}
            {t("home_featured.of")}{" "}
            <strong className="text-white">{totalCount}</strong> {t("home_featured.projects")}
          </p>
          <Link
            to="/portfolio"
            className="flex items-center gap-2 bg-electric-blue text-deep-black font-bold px-6 py-2.5 rounded-full hover:shadow-glow-blue transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
          >
            {t("home_featured.browse_portfolio")}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

      </div>
    </section>
  );
}